/**
 * Elements placed freely on a page: where they are, and where they snap.
 *
 * A report is a flow of sections, and a flow is right for most of it: text
 * follows text, a table follows its heading, and the page breaks where it must.
 * What a flow cannot do is a designed page, the kind that opens a chapter in a
 * published report: a photograph bleeding off one edge, a title set across it,
 * a figure pulled out into the margin. Those are placed, not flowed, and this
 * is how they are described.
 *
 * **Coordinates are millimetres from the top-left corner of the sheet**, the
 * trim corner, whatever the margins are. A designed page ignores the margins on
 * purpose, so measuring from them would put every picture in the wrong place
 * the day the margins changed. A sheet may be one page or two (a double page),
 * and the coordinates simply run across both.
 *
 * **Nothing is clamped to the page.** An element may stand partly or wholly
 * outside the sheet, because that is how a picture is made to bleed: it is set
 * past the edge and the printer cuts it there. What the canvas offers instead
 * is magnets (see `snapMove`), so the edges that matter, the trim, the bleed,
 * the margins and the centres, are easy to land on and just as easy to pass.
 *
 * Pure. Boxes and numbers in, boxes and numbers out; nothing measured on a
 * screen and nothing drawn.
 */

import { newChartContent, readChartContent, type ChartContent } from '@/domain/chart'
import { isJsonObject, readNumber, readString, type JsonValue } from '@/domain/json'
import { isAccent } from '@/domain/layout/colour'
import { parseMeasure } from '@/domain/layout/measure'

// ---------------------------------------------------------------------------
// What an element is
// ---------------------------------------------------------------------------

/** A rectangle on the sheet, in millimetres from its top-left corner. */
export interface FreeBox {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

export type FreeKind = 'text' | 'image' | 'shape' | 'chart'
export type FreeAlign = 'left' | 'center' | 'right' | 'justify'
export type ShapeForm = 'rectangle' | 'ellipse' | 'line'
export type ImageFit = 'cover' | 'contain'

export const FREE_KINDS: readonly FreeKind[] = ['text', 'image', 'shape', 'chart']
export const FREE_ALIGNS: readonly FreeAlign[] = ['left', 'center', 'right', 'justify']
export const SHAPE_FORMS: readonly ShapeForm[] = ['rectangle', 'ellipse', 'line']
export const IMAGE_FITS: readonly ImageFit[] = ['cover', 'contain']

interface FreeElementBase {
  readonly id: string
  /** Where the element stands before it is turned. */
  readonly box: FreeBox
  /**
   * How far the element is turned about its centre, in degrees clockwise,
   * between -180 and 180. The box is the unturned rectangle, so a turned
   * element keeps its own width and height and can be turned back exactly.
   */
  readonly rotation: number
  /** From 0, invisible, to 1. */
  readonly opacity: number
}

/** Words set in a box: a title across a picture, a caption, a pull quote. */
export interface FreeText extends FreeElementBase {
  readonly kind: 'text'
  /** Rich text, sanitised where it is drawn, as the text section's is. */
  readonly html: string
  /** In points. */
  readonly size: number
  readonly align: FreeAlign
  /** `#rrggbb`, or empty for the document's own ink. */
  readonly colour: string
  /** `#rrggbb` behind the words, or empty for none. */
  readonly fill: string
  /** Between the box's edge and the words, in millimetres. */
  readonly padding: number
  /**
   * A named text style the words are set in, or empty for none. A text placed
   * from an imported PDF wears the style it was printed in; see
   * `TextSection.textStyleId` for why the bare id is kept.
   */
  readonly textStyleId: string
}

export interface FreeImage extends FreeElementBase {
  readonly kind: 'image'
  /** A data URL, or empty while nothing has been chosen. */
  readonly source: string
  readonly fit: ImageFit
  /** Corner rounding, in millimetres. */
  readonly radius: number
}

export interface FreeShape extends FreeElementBase {
  readonly kind: 'shape'
  readonly form: ShapeForm
  /** `#rrggbb`, or empty for none. */
  readonly fill: string
  /** `#rrggbb`, or empty for none. */
  readonly stroke: string
  /** In points. */
  readonly strokeWidth: number
  /** Corner rounding of a rectangle, in millimetres. */
  readonly radius: number
}

/**
 * A chart standing where it was put, on a designed page: the figures of a
 * dashboard, say, which sit among the page's other elements rather than in
 * its flow. What it shows is the chart section's (see `chart.ts`).
 */
export interface FreeChart extends FreeElementBase {
  readonly kind: 'chart'
  /** What it is called in the list of elements: its title, as printed over it. Never drawn. */
  readonly name: string
  readonly chart: ChartContent
}

export type FreeElement = FreeText | FreeImage | FreeShape | FreeChart

/**
 * The class of a paragraph whose lines are kept as they were broken and set
 * flush on both sides, the last one included: what a justified paragraph
 * imported from a PDF is written as, its own last line following in a plain
 * paragraph. Its lines never wrap again, since a line wrapped again would
 * leave a word alone under it. Edited, the class may be lost and the lines
 * simply fall back to the box's alignment.
 */
export const JUSTIFIED_LINES = 'justified-lines'

// ---------------------------------------------------------------------------
// Limits
//
// Wide, because a designed page is allowed to be odd, and narrow enough that a
// stored number nobody meant cannot make the canvas unusable: a box a kilometre
// wide is a scroll bar nobody can get to the end of.
// ---------------------------------------------------------------------------

/** How far off the sheet an element may stand, in millimetres. */
export const FARTHEST_MM = 2000

/** Nothing is drawn smaller than this, or it cannot be picked up again. */
export const LEAST_MM = 2

const LARGEST_MM = 2000
const SIZE_PT = { min: 4, max: 200 }
const STROKE_PT = { min: 0, max: 20 }
const ROUNDING_MM = { min: 0, max: 500 }
const PADDING_MM = { min: 0, max: 100 }

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/** To a tenth of a millimetre: what a hand can place, and what stays readable in a file. */
export function tidy(value: number): number {
  return Math.round(value * 10) / 10
}

export function tidyBox(box: FreeBox): FreeBox {
  return { x: tidy(box.x), y: tidy(box.y), width: tidy(box.width), height: tidy(box.height) }
}

// ---------------------------------------------------------------------------
// Making and reading
// ---------------------------------------------------------------------------

/** What each kind starts as, before anybody has set it. */
export const DEFAULT_SIZES: Record<FreeKind, { width: number; height: number }> = {
  text: { width: 80, height: 24 },
  image: { width: 80, height: 60 },
  shape: { width: 60, height: 40 },
  chart: { width: 110, height: 70 },
}

/**
 * A new element of a kind, at a box, with an id handed in.
 *
 * The id is a parameter rather than made here so this module stays free of
 * everything that is not arithmetic; the kind folder that owns the section
 * has the id generator.
 */
export function newFreeElement(kind: FreeKind, id: string, box: FreeBox): FreeElement {
  const base = { id, box: tidyBox(box), rotation: 0, opacity: 1 }
  switch (kind) {
    case 'text':
      return {
        ...base,
        kind,
        html: '',
        size: 12,
        align: 'left',
        colour: '',
        fill: '',
        padding: 2,
        textStyleId: '',
      }
    case 'image':
      return { ...base, kind, source: '', fit: 'cover', radius: 0 }
    case 'shape':
      return {
        ...base,
        kind,
        form: 'rectangle',
        fill: '#e3f1f0',
        stroke: '',
        strokeWidth: 0.75,
        radius: 0,
      }
    case 'chart':
      return {
        ...base,
        kind,
        name: '',
        chart: newChartContent({ id: `${id}-1`, label: '', values: [0, 0, 0], colour: '' }),
      }
  }
}

/** A colour as `#rrggbb`, lower-cased, or empty for anything that is not one. */
function readColour(value: JsonValue | undefined, key: string): string {
  const text = readString(value, key)
  return isAccent(text) ? text.toLowerCase() : ''
}

function readBox(value: JsonValue | undefined): FreeBox {
  const box = isJsonObject(value) ? value : undefined
  return {
    x: clamp(readNumber(box, 'x'), -FARTHEST_MM, FARTHEST_MM),
    y: clamp(readNumber(box, 'y'), -FARTHEST_MM, FARTHEST_MM),
    width: clamp(readNumber(box, 'width', DEFAULT_SIZES.shape.width), LEAST_MM, LARGEST_MM),
    height: clamp(readNumber(box, 'height', DEFAULT_SIZES.shape.height), LEAST_MM, LARGEST_MM),
  }
}

function oneOf<T extends string>(options: readonly T[], value: string, fallback: T): T {
  return (options as readonly string[]).includes(value) ? (value as T) : fallback
}

/**
 * One element read back off a stored document, or nothing.
 *
 * Forgiving, field by field, because the document may have been written by an
 * earlier version or by hand. Only the kind cannot be forgiven: an element of
 * no known kind is nothing to draw, so it is dropped rather than guessed at.
 */
export function readFreeElement(value: JsonValue): FreeElement | null {
  if (!isJsonObject(value)) return null
  const kind = readString(value, 'kind')
  const id = readString(value, 'id')
  if (!id) return null

  const base = {
    id,
    box: readBox(value['box']),
    rotation: normaliseAngle(readNumber(value, 'rotation')),
    opacity: clamp(readNumber(value, 'opacity', 1), 0, 1),
  }

  if (kind === 'text') {
    return {
      ...base,
      kind,
      html: readString(value, 'html'),
      size: clamp(readNumber(value, 'size', 12), SIZE_PT.min, SIZE_PT.max),
      align: oneOf(FREE_ALIGNS, readString(value, 'align'), 'left'),
      colour: readColour(value, 'colour'),
      fill: readColour(value, 'fill'),
      padding: clamp(readNumber(value, 'padding', 2), PADDING_MM.min, PADDING_MM.max),
      textStyleId: readString(value, 'textStyleId'),
    }
  }

  if (kind === 'image') {
    return {
      ...base,
      kind,
      source: readString(value, 'source'),
      fit: oneOf(IMAGE_FITS, readString(value, 'fit'), 'cover'),
      radius: clamp(readNumber(value, 'radius'), ROUNDING_MM.min, ROUNDING_MM.max),
    }
  }

  if (kind === 'shape') {
    return {
      ...base,
      kind,
      form: oneOf(SHAPE_FORMS, readString(value, 'form'), 'rectangle'),
      fill: readColour(value, 'fill'),
      stroke: readColour(value, 'stroke'),
      strokeWidth: clamp(readNumber(value, 'strokeWidth', 0.75), STROKE_PT.min, STROKE_PT.max),
      radius: clamp(readNumber(value, 'radius'), ROUNDING_MM.min, ROUNDING_MM.max),
    }
  }

  if (kind === 'chart') {
    // A series stored without an id gets one made from the element's, so reading stays pure.
    let made = 0
    return {
      ...base,
      kind,
      name: readString(value, 'name').slice(0, 120),
      chart: readChartContent(value['chart'], () => `${id}-${++made}`),
    }
  }

  return null
}

// ---------------------------------------------------------------------------
// The sheet, and its guides
// ---------------------------------------------------------------------------

/** The measurements of the sheet elements are placed on, in millimetres. */
export interface SheetMetrics {
  /** One page across, or two for a double page. */
  readonly width: number
  readonly height: number
  /** One page across, whatever the sheet is. */
  readonly pageWidth: number
  readonly marginTop: number
  readonly marginSide: number
  readonly marginBottom: number
  readonly bleed: number
  readonly spread: boolean
}

/** The measurements a layout stores, as strings with their unit. */
export interface SheetMeasures {
  readonly pageWidth: string
  readonly pageHeight: string
  readonly marginTop: string
  readonly marginSide: string
  readonly marginBottom: string
  readonly bleed: string
}

/**
 * The sheet, read off the document's layout.
 *
 * A layout is read through its rules, so its measurements always parse; the
 * fallbacks are for a caller handing over something no layout would hold.
 */
export function sheetMetricsOf(layout: SheetMeasures, spread: boolean): SheetMetrics {
  const mm = (value: string, fallback: number): number => parseMeasure(value) ?? fallback
  const pageWidth = mm(layout.pageWidth, 210)
  return {
    width: spread ? pageWidth * 2 : pageWidth,
    height: mm(layout.pageHeight, 297),
    pageWidth,
    marginTop: mm(layout.marginTop, 22),
    marginSide: mm(layout.marginSide, 20),
    marginBottom: mm(layout.marginBottom, 24),
    bleed: mm(layout.bleed, 0),
    spread,
  }
}

/**
 * What a guide is: the edge of the paper, the bleed past it, a margin, the
 * middle of a page, the fold of a double page, or the edge of another element.
 */
export type GuideRole = 'trim' | 'bleed' | 'margin' | 'centre' | 'fold' | 'element'

/** A line on the sheet an edge can snap to. */
export interface Guide {
  readonly axis: 'x' | 'y'
  /** Where it stands, in millimetres from the sheet's top-left corner. */
  readonly at: number
  readonly role: GuideRole
}

/**
 * The guides a sheet offers on its own.
 *
 * The bleed is among them and stands *outside* the trim: it is where a
 * picture meant to run off the page should end, so it is where one snaps to.
 * A double page has a fold down its middle and a margin either side of it,
 * because facing pages keep their inner margins.
 */
export function sheetGuides(sheet: SheetMetrics): Guide[] {
  const x = (at: number, role: GuideRole): Guide => ({ axis: 'x', at, role })
  const y = (at: number, role: GuideRole): Guide => ({ axis: 'y', at, role })

  const guides: Guide[] = [
    x(0, 'trim'),
    x(sheet.width, 'trim'),
    y(0, 'trim'),
    y(sheet.height, 'trim'),
    x(sheet.marginSide, 'margin'),
    x(sheet.width - sheet.marginSide, 'margin'),
    y(sheet.marginTop, 'margin'),
    y(sheet.height - sheet.marginBottom, 'margin'),
    y(sheet.height / 2, 'centre'),
  ]

  if (sheet.bleed > 0) {
    guides.push(
      x(-sheet.bleed, 'bleed'),
      x(sheet.width + sheet.bleed, 'bleed'),
      y(-sheet.bleed, 'bleed'),
      y(sheet.height + sheet.bleed, 'bleed'),
    )
  }

  if (sheet.spread) {
    const fold = sheet.pageWidth
    guides.push(
      x(fold, 'fold'),
      x(fold - sheet.marginSide, 'margin'),
      x(fold + sheet.marginSide, 'margin'),
      x(fold / 2, 'centre'),
      x(fold + fold / 2, 'centre'),
    )
  } else {
    guides.push(x(sheet.width / 2, 'centre'))
  }

  return guides
}

/**
 * The edges and centres of the elements that are not moving, for lining the
 * moving ones up with them.
 *
 * A turned element offers the edges of the rectangle it covers on the page,
 * since that is what an eye lines up against.
 */
export function elementGuides(
  elements: readonly FreeElement[],
  moving: ReadonlySet<string>,
): Guide[] {
  const guides: Guide[] = []
  for (const element of elements) {
    if (moving.has(element.id)) continue
    const { x, y, width, height } = boundsOf(element.box, element.rotation)
    guides.push(
      { axis: 'x', at: x, role: 'element' },
      { axis: 'x', at: x + width / 2, role: 'element' },
      { axis: 'x', at: x + width, role: 'element' },
      { axis: 'y', at: y, role: 'element' },
      { axis: 'y', at: y + height / 2, role: 'element' },
      { axis: 'y', at: y + height, role: 'element' },
    )
  }
  return guides
}

// ---------------------------------------------------------------------------
// Snapping
// ---------------------------------------------------------------------------

/** A box after snapping, and the guides it landed on, for drawing them. */
export interface Snapped {
  readonly box: FreeBox
  readonly guides: readonly Guide[]
}

interface Pull {
  readonly delta: number
  readonly guide: Guide
}

/** The nearest guide on an axis to any of some positions, within reach. */
function nearest(
  guides: readonly Guide[],
  axis: 'x' | 'y',
  positions: readonly number[],
  reach: number,
): Pull | null {
  let best: Pull | null = null
  for (const guide of guides) {
    if (guide.axis !== axis) continue
    for (const position of positions) {
      const delta = guide.at - position
      if (Math.abs(delta) > reach) continue
      if (best === null || Math.abs(delta) < Math.abs(best.delta)) best = { delta, guide }
    }
  }
  return best
}

/**
 * A box being moved, pulled onto the nearest guides.
 *
 * Each axis on its own: the left, middle or right edge lands on a vertical
 * guide while the top, middle or bottom lands on a horizontal one, and the
 * two do not have to be the same kind of guide. Within `reach` only, which
 * the caller sets from the zoom, so a magnet feels the same size on screen
 * whatever the paper is scaled to. Past it, the box goes where it is put.
 */
export function snapMove(box: FreeBox, guides: readonly Guide[], reach: number): Snapped {
  const across = nearest(guides, 'x', [box.x, box.x + box.width / 2, box.x + box.width], reach)
  const down = nearest(guides, 'y', [box.y, box.y + box.height / 2, box.y + box.height], reach)

  return {
    box: {
      ...box,
      x: box.x + (across?.delta ?? 0),
      y: box.y + (down?.delta ?? 0),
    },
    guides: [across?.guide, down?.guide].filter((guide): guide is Guide => guide !== undefined),
  }
}

/** The corner or edge being dragged, named by compass point. */
export type Handle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'

export const HANDLES: readonly Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']

/**
 * A box resized by dragging one of its handles.
 *
 * The opposite edge or corner stays where it was; that is what makes a drag
 * of the right edge feel like the right edge moving. With `keepRatio` the
 * other dimension follows, from the width on side handles and from whichever
 * moved more on corners, so a photograph stays a photograph. Nothing goes
 * below `least`, or the box could not be picked up again.
 */
export function resizeBox(
  box: FreeBox,
  handle: Handle,
  dx: number,
  dy: number,
  keepRatio: boolean,
  least = LEAST_MM,
): FreeBox {
  const east = handle.includes('e')
  const west = handle.includes('w')
  const south = handle.includes('s')
  const north = handle.includes('n')

  let width = box.width + (east ? dx : west ? -dx : 0)
  let height = box.height + (south ? dy : north ? -dy : 0)

  if (keepRatio && box.height > 0) {
    const ratio = box.width / box.height
    const sideways = east || west
    const upright = north || south
    if (sideways && upright) {
      // A corner: whichever way the hand moved further leads.
      if (Math.abs(width - box.width) >= Math.abs(height - box.height)) height = width / ratio
      else width = height * ratio
    } else if (sideways) {
      height = width / ratio
    } else {
      width = height * ratio
    }
  }

  width = Math.max(width, least)
  height = Math.max(height, least)

  return {
    x: west ? box.x + box.width - width : box.x,
    y: north ? box.y + box.height - height : box.y,
    width,
    height,
  }
}

/**
 * A box being resized, its moving edges pulled onto the nearest guides.
 *
 * Only the edges the handle moves: dragging the right edge never shifts the
 * left one, whatever guide the left one is near.
 */
export function snapResize(
  box: FreeBox,
  handle: Handle,
  guides: readonly Guide[],
  reach: number,
  least = LEAST_MM,
): Snapped {
  let { x, y, width, height } = box
  const landed: Guide[] = []

  if (handle.includes('e')) {
    const pull = nearest(guides, 'x', [x + width], reach)
    if (pull) {
      width = Math.max(width + pull.delta, least)
      landed.push(pull.guide)
    }
  } else if (handle.includes('w')) {
    const pull = nearest(guides, 'x', [x], reach)
    if (pull) {
      const right = x + width
      x = Math.min(x + pull.delta, right - least)
      width = right - x
      landed.push(pull.guide)
    }
  }

  if (handle.includes('s')) {
    const pull = nearest(guides, 'y', [y + height], reach)
    if (pull) {
      height = Math.max(height + pull.delta, least)
      landed.push(pull.guide)
    }
  } else if (handle.includes('n')) {
    const pull = nearest(guides, 'y', [y], reach)
    if (pull) {
      const bottom = y + height
      y = Math.min(y + pull.delta, bottom - least)
      height = bottom - y
      landed.push(pull.guide)
    }
  }

  return { box: { x, y, width, height }, guides: landed }
}

// ---------------------------------------------------------------------------
// Placing without dragging
// ---------------------------------------------------------------------------

export function nudge(box: FreeBox, dx: number, dy: number): FreeBox {
  return { ...box, x: box.x + dx, y: box.y + dy }
}

/** A box of a size, in the middle of the sheet. */
export function centred(sheet: SheetMetrics, size: { width: number; height: number }): FreeBox {
  return tidyBox({
    x: (sheet.width - size.width) / 2,
    y: (sheet.height - size.height) / 2,
    width: size.width,
    height: size.height,
  })
}

export type Alignment = 'left' | 'centre' | 'right' | 'top' | 'middle' | 'bottom'

export const ALIGNMENTS: readonly Alignment[] = [
  'left',
  'centre',
  'right',
  'top',
  'middle',
  'bottom',
]

/** The whole sheet as a box, trim to trim: what one element is aligned to. */
export function sheetFrame(sheet: SheetMetrics): FreeBox {
  return { x: 0, y: 0, width: sheet.width, height: sheet.height }
}

/**
 * Where an element goes to be flush with an edge of a frame, or its middle.
 *
 * The frame is the sheet for one element, trim to trim rather than margin to
 * margin, since a designed page is composed to the paper and the magnets
 * already offer the margins; and it is the rectangle around them all for
 * several, so they line up with one another. A turned element is aligned by
 * the rectangle it covers, which is what the eye lines up.
 */
export function alignTo(element: FreeElement, frame: FreeBox, how: Alignment): FreeBox {
  const covered = boundsOf(element.box, element.rotation)
  let dx = 0
  let dy = 0
  switch (how) {
    case 'left':
      dx = frame.x - covered.x
      break
    case 'centre':
      dx = frame.x + frame.width / 2 - (covered.x + covered.width / 2)
      break
    case 'right':
      dx = frame.x + frame.width - (covered.x + covered.width)
      break
    case 'top':
      dy = frame.y - covered.y
      break
    case 'middle':
      dy = frame.y + frame.height / 2 - (covered.y + covered.height / 2)
      break
    case 'bottom':
      dy = frame.y + frame.height - (covered.y + covered.height)
      break
  }
  return nudge(element.box, dx, dy)
}

// ---------------------------------------------------------------------------
// Turning
// ---------------------------------------------------------------------------

/** An angle in degrees, brought between -180 (excluded) and 180, to a tenth. */
export function normaliseAngle(degrees: number): number {
  if (!Number.isFinite(degrees)) return 0
  let angle = degrees % 360
  if (angle > 180) angle -= 360
  if (angle <= -180) angle += 360
  const tidied = Math.round(angle * 10) / 10
  // Negative zero is a different number from zero once written to a file.
  return tidied === 0 ? 0 : tidied
}

function radians(degrees: number): number {
  return (degrees * Math.PI) / 180
}

/**
 * The upright rectangle a turned box covers on the page.
 *
 * What a turned element is snapped, aligned and lined up by: its own edges
 * run at an angle, and no guide could meet them.
 */
export function boundsOf(box: FreeBox, rotation: number): FreeBox {
  if (rotation === 0) return box
  const cos = Math.abs(Math.cos(radians(rotation)))
  const sin = Math.abs(Math.sin(radians(rotation)))
  const width = box.width * cos + box.height * sin
  const height = box.width * sin + box.height * cos
  const centreX = box.x + box.width / 2
  const centreY = box.y + box.height / 2
  return { x: centreX - width / 2, y: centreY - height / 2, width, height }
}

/**
 * The angle a point makes about a centre, in degrees clockwise from straight
 * up: the angle an element is turned to when its turning handle, which sits
 * above it, is dragged to that point.
 */
export function angleFrom(
  centre: { readonly x: number; readonly y: number },
  point: { readonly x: number; readonly y: number },
): number {
  return normaliseAngle((Math.atan2(point.x - centre.x, centre.y - point.y) * 180) / Math.PI)
}

/** How near a quarter turn an angle has to come to be pulled onto it, in degrees. */
export const ANGLE_REACH = 3

/** The steps a turn is held to when asked, in degrees. */
export const ANGLE_STEP = 15

/**
 * An angle held to steps, or pulled onto the nearest quarter turn.
 *
 * With a step it goes from step to step and nowhere between, which is how
 * somebody turns a title by exactly thirty degrees. Without one it is free,
 * except that a quarter turn within reach is taken: an element meant to be
 * upright is almost never left two degrees off on purpose.
 */
export function snapAngle(angle: number, step: number | null, reach: number): number {
  if (step !== null && step > 0) return normaliseAngle(Math.round(angle / step) * step)
  const nearest = Math.round(angle / 90) * 90
  return normaliseAngle(Math.abs(angle - nearest) <= reach ? nearest : angle)
}

/**
 * A turned box resized by one of its handles.
 *
 * The handles turn with the element, so the hand moves in the element's own
 * frame: the pointer's travel is turned back into that frame, the box resized
 * there exactly as an upright one would be, and the change of centre turned
 * back out onto the page. The edge or corner opposite the handle stays where
 * it is on the page, which is what makes a turned element resize the way it
 * looks like it should.
 */
export function resizeRotated(
  box: FreeBox,
  rotation: number,
  handle: Handle,
  dx: number,
  dy: number,
  keepRatio: boolean,
  least = LEAST_MM,
): FreeBox {
  if (rotation === 0) return resizeBox(box, handle, dx, dy, keepRatio, least)

  const cos = Math.cos(radians(rotation))
  const sin = Math.sin(radians(rotation))

  const alongX = dx * cos + dy * sin
  const alongY = -dx * sin + dy * cos
  const local = resizeBox(
    { x: 0, y: 0, width: box.width, height: box.height },
    handle,
    alongX,
    alongY,
    keepRatio,
    least,
  )

  const shiftX = local.x + local.width / 2 - box.width / 2
  const shiftY = local.y + local.height / 2 - box.height / 2
  const centreX = box.x + box.width / 2 + shiftX * cos - shiftY * sin
  const centreY = box.y + box.height / 2 + shiftX * sin + shiftY * cos

  return {
    x: centreX - local.width / 2,
    y: centreY - local.height / 2,
    width: local.width,
    height: local.height,
  }
}

// ---------------------------------------------------------------------------
// Several at once
// ---------------------------------------------------------------------------

/** The smallest upright rectangle around some boxes, or nothing for none. */
export function unionOf(boxes: readonly FreeBox[]): FreeBox | null {
  if (boxes.length === 0) return null
  const left = Math.min(...boxes.map((box) => box.x))
  const top = Math.min(...boxes.map((box) => box.y))
  const right = Math.max(...boxes.map((box) => box.x + box.width))
  const bottom = Math.max(...boxes.map((box) => box.y + box.height))
  return { x: left, y: top, width: right - left, height: bottom - top }
}

/** The rectangle some elements cover together, turned ones included. */
export function coveredBy(elements: readonly FreeElement[]): FreeBox | null {
  return unionOf(elements.map((element) => boundsOf(element.box, element.rotation)))
}

/**
 * Elements spread so the gaps between them are equal, along one axis.
 *
 * The first and the last stay where they are and the others are placed
 * between them, in the order they already stand. Fewer than three have no
 * gaps to even out, so nothing moves. Returns the new box of each element by
 * id, for the caller to write as one change.
 */
export function distribute(
  elements: readonly FreeElement[],
  axis: 'x' | 'y',
): ReadonlyMap<string, FreeBox> {
  const moved = new Map<string, FreeBox>()
  if (elements.length < 3) return moved

  const start = (covered: FreeBox): number => (axis === 'x' ? covered.x : covered.y)
  const size = (covered: FreeBox): number => (axis === 'x' ? covered.width : covered.height)

  const ordered = elements
    .map((element) => ({ element, covered: boundsOf(element.box, element.rotation) }))
    .sort((a, b) => start(a.covered) - start(b.covered))

  const first = ordered[0]
  const last = ordered.at(-1)
  if (!first || !last) return moved

  const span = start(last.covered) + size(last.covered) - start(first.covered)
  const filled = ordered.reduce((total, item) => total + size(item.covered), 0)
  const gap = (span - filled) / (ordered.length - 1)

  let at = start(first.covered)
  for (const { element, covered } of ordered) {
    const shift = at - start(covered)
    moved.set(element.id, nudge(element.box, axis === 'x' ? shift : 0, axis === 'y' ? shift : 0))
    at += size(covered) + gap
  }
  return moved
}

// ---------------------------------------------------------------------------
// Pictures
// ---------------------------------------------------------------------------

/** What a picture is taken to be when its proportions cannot be read. */
const FALLBACK_ASPECT = 4 / 3

/**
 * The box a picture dropped on the page takes: centred where it was dropped,
 * as wide as a picture added from the panel, in its own proportions, and
 * never taller than the space between the margins.
 */
export function imageBoxAt(
  point: { readonly x: number; readonly y: number },
  aspect: number,
  sheet: SheetMetrics,
): FreeBox {
  const ratio = Number.isFinite(aspect) && aspect > 0 ? aspect : FALLBACK_ASPECT
  const room = Math.max(sheet.height - sheet.marginTop - sheet.marginBottom, LEAST_MM)

  let width = Math.min(DEFAULT_SIZES.image.width, sheet.pageWidth - sheet.marginSide * 2)
  let height = width / ratio
  if (height > room) {
    height = room
    width = height * ratio
  }

  return tidyBox({ x: point.x - width / 2, y: point.y - height / 2, width, height })
}

/**
 * A box given a picture's proportions: as wide as it was, as tall as the
 * picture needs, about the same middle, so choosing a portrait photograph for
 * a landscape frame does not squash it.
 */
export function fittedToImage(box: FreeBox, aspect: number): FreeBox {
  if (!Number.isFinite(aspect) || aspect <= 0) return box
  const height = Math.max(box.width / aspect, LEAST_MM)
  return tidyBox({ ...box, y: box.y + (box.height - height) / 2, height })
}

// ---------------------------------------------------------------------------
// The list as a whole
// ---------------------------------------------------------------------------

/**
 * Elements are drawn in order, so the last one is on top. Moving one a step
 * up the list brings it forward; a step down sends it back.
 */
export function reorder(
  elements: readonly FreeElement[],
  id: string,
  direction: -1 | 1,
): FreeElement[] {
  const at = elements.findIndex((element) => element.id === id)
  const to = at + direction
  if (at < 0 || to < 0 || to >= elements.length) return [...elements]
  const next = [...elements]
  const [moved] = next.splice(at, 1)
  if (moved) next.splice(to, 0, moved)
  return next
}

/**
 * A list with some of its elements moved, together, to a gap: before the one
 * at `gap` in the list as it was, or to its end. Those moved keep their order
 * among themselves, so several dragged at once land as they were stacked.
 * Any list will do; the layer's list of elements is the front one first.
 */
export function restack<T extends { readonly id: string }>(
  list: readonly T[],
  moving: ReadonlySet<string>,
  gap: number,
): T[] {
  const kept = list.filter((element) => !moving.has(element.id))
  const moved = list.filter((element) => moving.has(element.id))
  const at = list.slice(0, Math.max(0, gap)).filter((element) => !moving.has(element.id)).length
  return [...kept.slice(0, at), ...moved, ...kept.slice(at)]
}

/** How far a copy is set from its original, so the two can be told apart. */
export const COPY_OFFSET_MM = 5

/** A copy of an element, with a new id, set just off the original and placed above it. */
export function copyOf(element: FreeElement, id: string): FreeElement {
  return { ...element, id, box: nudge(element.box, COPY_OFFSET_MM, COPY_OFFSET_MM) }
}

/** Past this many steps a paste stops looking for a clear place and lands where it is. */
const MOST_PASTE_STEPS = 40

/**
 * Copied elements pasted into a layer, under new ids.
 *
 * Where they were copied from, which is right for pasting onto another page
 * laid out like the first. When one of them would land exactly on an element
 * already there, which is what pasting back into the same layer does, they
 * all step down and to the right together until none does, so every paste can
 * be seen and the copies keep their places relative to one another.
 */
export function pasted(
  copied: readonly FreeElement[],
  existing: readonly FreeElement[],
  makeId: () => string,
): FreeElement[] {
  const key = (box: FreeBox): string => `${tidy(box.x)}:${tidy(box.y)}:${box.width}:${box.height}`
  const taken = new Set(existing.map((element) => key(element.box)))

  let offset = 0
  for (let step = 0; step < MOST_PASTE_STEPS; step += 1) {
    const collides = copied.some((element) => taken.has(key(nudge(element.box, offset, offset))))
    if (!collides) break
    offset += COPY_OFFSET_MM
  }

  return copied.map((element) => ({
    ...element,
    id: makeId(),
    box: tidyBox(nudge(element.box, offset, offset)),
  }))
}
